/**
 * **The only module under `web/src/` that imports deepscatter** (`oop_contracts §8`, `V4`, `V6`).
 *
 * Two independent reasons for the boundary, either sufficient:
 *
 * - It makes the CC BY-NC-SA carve-out a **directory boundary** rather than a grep. `test/
 *   boundary.test.ts` enforces it.
 * - It makes regl-scatterplot (MIT, ~20 M points, `pointOver` carrying `pointIndex`) a drop-in
 *   replacement if the NC term ever becomes a problem, instead of a rewrite.
 *
 * **Interaction mode lives above this interface, not inside it.** This module reports *where* a
 * click landed; `main.ts` decides whether that means "select" or "decode". A renderer that knew
 * about modes would have to be reimplemented by any replacement, which is the escape hatch the
 * boundary exists to keep open.
 *
 * ## The 3.x API hazards, handled once (P9.2, `plan §7.4`)
 *
 * The 3.x line breaks against 2.x in four ways that produce confusing errors when a stale 2.x
 * example is copied. Each is handled here so nothing downstream meets it:
 *
 * | Hazard | What it looks like | Handled |
 * |---|---|---|
 * | **Named export** | `import Scatterplot from 'deepscatter'` gives `undefined` | `import { Scatterplot }` below |
 * | **Arrow is a peer dependency** | two copies of `apache-arrow` and `instanceof` failures | imported and **re-exported** by us, so the app never installs its own |
 * | **`QuadTile`/`ArrowTile` → `Tile`, `QuadTileDataset`/`ArrowDataset` → `Deeptable`** | the old names do not exist | only `Deeptable` and `Tile` are named here |
 * | **String lambdas to `plotAPI` removed** (they were `eval`-based) | a string argument is silently ignored | every handler below is a real function |
 *
 * ## Four places the published types disagree with the runtime (`V52`, `plan §11` open point 8)
 *
 * **A green `tsc` against this library is evidence about its declarations, not about the
 * program.** Every one of these was found by running it, and each is contained here:
 *
 * 1. `plotAPI(prefs)` is declared as taking `APICall`, which has no `source_url`, and **throws at
 *    runtime without one** on the first call.
 * 2. `Deeptable.fromQuadfeather` is `async` while declared as returning a `Deeptable`.
 * 3. `tileProxy` is declared as supported and implemented as `throw new Error('Not yet
 *    supported')`. Tile authentication therefore rides the session cookie on a plain `fetch`,
 *    which `V8`'s single-origin deployment already provides.
 * 4. **`set click_function` is declared as `(d: StructRowProxy, plot) => void` and is called with
 *    a `Qid`** — `ClickFunction extends SettableFunction<void, Qid>`, and the runtime line is
 *    `click_handler.f(dd.qid, this.scatterplot)`. A handler written to the declaration reads
 *    `.sequence` off a two-element array and gets `undefined`. Found at P9.1; this module does
 *    not use that setter at all, for the reason in `attachClicks` below.
 */
import type { RecordBatch, StructRowProxy, Table } from 'apache-arrow';
import { Scatterplot } from 'deepscatter';
import {
  DEFAULT_POINT_SIZE,
  DEFAULT_ZOOM_BALANCE,
  DRAG_RESIZE_CORNER_PX,
  DRAG_THRESHOLD_PX,
  clampDrag,
  dragLimitPx,
  dragRange,
  type DragRange,
  FLIP_CLEARANCE_PX,
  markSizePx,
} from './marks';

// Arrow is deepscatter's **peer** dependency, so the app must not import its own copy: two copies
// mean two class identities and `instanceof` silently false. Re-exported here so `api.ts` and the
// panels get ours (`plan §7.4`).
export type { RecordBatch, StructRowProxy, Table };

/** `AtlasManifest.columns[].role` (`oop_contracts §1.1`). The UI renders by role, never by name. */
export type ColumnRole = 'index' | 'categorical' | 'continuous' | 'boolean';

/**
 * One point of a curated overlay (`V153`, P31).
 *
 * **The renderer builds the node**, unlike `mark`, which takes one. A curated glyph has exactly
 * one shape — a circle with an optional label — so letting the caller supply arbitrary DOM would
 * buy nothing and would put the overlay's structure in two files. `setClass` is the only thing
 * that varies, and it varies over three known values.
 */
export interface CuratedPoint {
  readonly id: string;
  readonly xy: [number, number];
  /** A CSS class naming the set, which is what gives the circle its colour. */
  readonly setClass: string;
  /** The text to print beside the dot, or `null` to draw the dot alone. */
  readonly label: string | null;
  /** The full label, shown on hover. Always present, even when `label` is `null`. */
  readonly title: string;
}

/**
 * One rendered point, read **out of the tile datum** (`V13`).
 *
 * **`row` is the identity and `ix` is the rendering rank** (`V51`). §8 drew only `ix`, before that
 * split existed; both are carried and `row` is what goes to the server. On a subsampled view they
 * name different peptides, so a caller that reaches for `ix` gets a different peptide silently.
 * `ix` is `uint64` in the tiles, which arrow-js surfaces as a **BigInt**, so it is converted here
 * once rather than at every use.
 */
export interface PointDatum {
  row: number;
  ix: number;
  x: number;
  y: number;
  sequence: string;
  [column: string]: unknown;
}

export interface LatentRenderer {
  /**
   * Point the plot at a tile directory.
   *
   * `columns` names everything the app will want to read off a clicked datum. **It is not an
   * optimisation, it is what makes `V13` true** — see `IDENTITY_COLUMNS` below.
   */
  setDeeptable(sourceUrl: string, columns?: string[]): Promise<void>;
  /**
   * Point the plot at an **untiled** Arrow IPC buffer — a raw-dimension pair (`V12`, P11.3).
   *
   * `key` names the view for the same cache `setDeeptable` uses, so switching back to a pair
   * already loaded costs nothing. It is the projection name (`raw:<i>,<j>`), which is exactly
   * what a tile directory's URL is to the tiled path: a stable identity for one set of points.
   *
   * **No `columns` parameter, and that asymmetry is the point.** A tiled deeptable downloads
   * columns lazily, which is why `setDeeptable` has to name them (`V61`); an Arrow buffer arrived
   * whole, so every column is already in the batch and there is nothing to prefetch.
   */
  setArrowBuffer(key: string, buffer: Uint8Array): Promise<void>;
  setColorChannel(column: string, role: ColumnRole): void;
  /**
   * Paint every corpus point one flat colour (`V154`, P31).
   *
   * **The `none` the colour select never had.** Its options are built from `manifest.columns`, so
   * before this the map was always coloured *by something* — and `V84` already notes the failure
   * that produces when the something has one distinct value. What this adds is the honest case:
   * the reader does not want the corpus to carry information right now, because they are looking
   * at what is drawn on top of it.
   *
   * **Not the same as never calling `setColorChannel`.** The channel persists across projection
   * switches (`setDeeptable` re-applies it), so "stop colouring" has to be said, not merely not
   * said.
   */
  clearColorChannel(): void;
  /**
   * The landmark overlay (`V18`): draw the points where `column` is true in the foreground.
   *
   * **§8 typed this `setHighlight(ix: number[])` and it takes a column name instead.** The
   * landmark set this exists for is `is_seed`, which is a **column of every tile** (`V13`) — so
   * passing rows would mean asking the server for a list the tile already carries, on a path whose
   * whole guarantee is that it needs no server (§3.0). `null` clears it.
   */
  setHighlight(column: string | null): void;
  /**
   * Restrict what is drawn to the rows where `column` is one of `values` (P10.5).
   *
   * The dataset selector — full / train / val / test — is **a filter on the `split` column, not a
   * separate atlas** (`plan §4`): the splits are one partition of one corpus, and three atlases
   * would triple the disk and make the three views non-comparable. `null` clears it.
   */
  setFilter(column: string | null, values?: readonly unknown[]): void;
  /** The rows `setHighlight` is currently drawing as landmarks, for a caller that lists them. */
  landmarks(): PointDatum[];
  /**
   * Stick `node` to a point's data coordinates until it is removed (`V112`, `V116`).
   *
   * **Placement only — this interface still has no opinion about what the node says** (§8). The
   * caller builds the card; the renderer knows the zoom transform, which is the one thing a
   * caller cannot compute.
   *
   * **Keyed by `datum.row`, and many can be pinned at once** (`V116`). Pinning a row that is
   * already pinned replaces its node rather than adding a second one, so the map cannot end up
   * with two cards for one peptide however the caller is driven.
   */
  pin(datum: PointDatum, node: HTMLElement): void;
  /** Remove one row's node. A row that is not pinned is not an error. */
  unpin(row: number): void;
  /** Remove every pinned node. */
  clearPins(): void;
  /** The rows currently pinned, in the order they were pinned. */
  pinnedRows(): number[];
  /**
   * Stick a node to a **bare coordinate**, for a point that is not in the corpus (`V123`, P20.2).
   *
   * The same placement contract as `pin`, and deliberately a separate one: a decoded point has no
   * `row`, because nothing was looked up to produce it. `id` is the caller's own identity for it.
   * Placing an id that is already marked replaces its node rather than adding a second.
   *
   * **Its own overlay, so `clearPins()` does not take these with it.** Pinned cards are cleared on
   * every projection change; marks are re-filtered for the new view instead, and a mark that was
   * destroyed could not be regenerated.
   */
  mark(id: string, xy: [number, number], node: HTMLElement): void;
  /** Remove one mark. An id that is not marked is not an error. */
  unmark(id: string): void;
  /** Remove every mark. */
  clearMarks(): void;
  /** The ids currently marked, in the order they were marked. */
  markIds(): string[];

  /**
   * Replace the whole curated overlay in one call (`V153`, P31).
   *
   * **Bulk, and a separate overlay from `mark`, for two measured reasons.** `mark` is built for a
   * card: it attaches drag listeners, remembers a dragged width and offset, measures the node's
   * extent to decide whether to flip it, and re-places the entire set on every insert. Adding 206
   * curated points through it would attach 206 pointer-handler sets nothing can drag, and cost
   * 206 full placement passes — O(n²) — at load.
   *
   * A curated point is a dot with an optional label. It never drags, never flips and never
   * resizes, so its placement is a `transform` write and nothing else: no layout reads at all,
   * where `place` does two per node per frame.
   *
   * **Replace rather than add**, because the caller's state is the whole set: three toggles and a
   * precedence rule produce a list, and diffing it here by id is cheaper and less error-prone
   * than asking the caller to track which points it has already drawn.
   */
  setCuratedPoints(points: readonly CuratedPoint[]): void;
  /** The ids currently drawn as curated points, in insertion order. For tests and for counting. */
  curatedIds(): string[];
  onPointClick(cb: (datum: PointDatum) => void): void;
  onEmptyClick(cb: (xy: [number, number]) => void): void;
  nearestRendered(xy: [number, number]): PointDatum | null;
  destroy(): void;
}

/**
 * How many points to draw before zooming in for more.
 *
 * **A rendering budget, not a corpus size** — and after `V51` it can be, because the tiles' `ix`
 * is a dense rank rather than the atlas row index. deepscatter loads the first `max_points` by
 * rank and fetches more as `k` rises (`max_ix = max_points·k²/(k^zoom_balance)²`), so this is
 * "how many points am I willing to draw at once" and is independent of `n_rows`.
 */
export const RENDER_BUDGET = 100_000;

/**
 * How long `setHighlight` keeps re-placing the landmarks after a view changes (`V111`, `V115`).
 *
 * 12 x 250 ms = 3 s. It has to outlast two different settling times — the tiles arriving, and the
 * zoom adopting the new projection's extent — and 3 s covers both for a resident atlas whose root
 * tile the browser has already cached. Each tick is eight `transform` strings unless the set is
 * still empty, so the window is cheap to make generous.
 */
const LANDMARK_SETTLE_TICKS = 12;
const LANDMARK_SETTLE_MS = 250;

/**
 * How long the search for the rows goes on after that window closes, while it has found none
 * (`V149`).
 *
 * **The 3 s above is a budget for a race, and the reader met the case where it runs out**: on a
 * first load of a projection the landmarks did not appear at all until the selector was touched,
 * which calls `setHighlight` again. Measured locally against a warm cache the rows were found at
 * ~4 s from navigation — so the margin is small before a cold tile fetch or a first `POST /load`
 * of a resident atlas takes it past the end.
 *
 * **Doubling from 500 ms and stopping at 16 s**, so the whole hunt is 0.5 + 1 + 2 + 4 + 8 + 16 =
 * 31.5 s of coverage in **six** scans. The backoff is the point: a fixed 250 ms tick extended to
 * the same horizon would be 126 scans, and the scan is the expensive half — it walks every row of
 * every visible tile. Six is a cost an atlas with no true rows anywhere can pay once per view
 * change, which is what makes this bounded rather than a poll.
 */
const LANDMARK_HUNT_FIRST_MS = 500;
const LANDMARK_HUNT_MAX_MS = 16_000;

/**
 * The same window for the decoded marks, at the same tick length (`V123`, P20.2).
 *
 * Shorter than the landmarks' because only half the problem applies: there is nothing to re-scan,
 * so this window has to outlast the zoom adopting the new extent and nothing else. Each tick is
 * one `transform` string per mark.
 */
const MARK_SETTLE_TICKS = 8;

/**
 * One node stuck to a data coordinate — a pinned card or a decoded mark.
 *
 * `extent` is how far it reaches past its anchor, cached because measuring it is a layout read and
 * because re-measuring a flipped node oscillates. See `measure`.
 */
interface Placed {
  xy: [number, number];
  node: HTMLElement;
  extent: { width: number; height: number };
  /**
   * Where the reader has dragged the card, in CSS pixels from its resting place (`V128`).
   *
   * **Stored unclamped.** `applyOffset` clamps what is drawn, so zooming out pulls a card back
   * towards its point and zooming in restores the arrangement. Clamping the stored value would
   * destroy a layout on one stray scroll.
   */
  offset: { x: number; y: number };
  /** The `[data-drag]` element the caller marked, or `null` if this node has none. */
  handle: HTMLElement | null;
  /**
   * The width the reader dragged this card to, as an inline CSS length, or `''` (`V135`).
   *
   * **The same problem `offset` solves, for the other thing the reader can do to a card.**
   * `resize: horizontal` is a user-agent affordance: the browser writes an inline `width` onto the
   * element and nothing tells the application it happened. A card is *rebuilt* rather than patched
   * on every trajectory change, so that inline width went into the bin with the old node — and a
   * 16-mer the reader had widened a card to fit re-wrapped on the next mode switch.
   *
   * Kept here rather than read off the DOM at replacement time alone, so it also survives a decoded
   * card being closed and reopened, where the element carrying the width is destroyed in between.
   */
  width: string;
  /**
   * Where the handle rests relative to the anchor, before any drag, in CSS pixels (`V130`).
   *
   * **The drag bound is a distance from the *point*, so it cannot be written without this.** The
   * card rests down and to the right of its anchor by half its own body; a bound applied to the
   * raw offset is therefore a box around that resting corner rather than around the point. `null`
   * until the first measurement, where `applyOffset` falls back to a symmetric box.
   */
  rest: { x: number; y: number; width: number; height: number } | null;
  /** Undo for the drag listeners, so a replaced or removed node does not leak a set. */
  detachDrag: (() => void) | null;
}

/** A freshly placed entry, before its first measurement or drag. */
const placed = (
  xy: [number, number],
  node: HTMLElement,
  offset = { x: 0, y: 0 },
  width = '',
): Placed => ({
  xy,
  node,
  extent: { width: 0, height: 0 },
  offset,
  handle: null,
  rest: null,
  detachDrag: null,
  width,
});

/**
 * Columns a datum must carry before it can answer a click, and **the reason `V13` needs help.**
 *
 * `V13` puts the sequence in the tile so that a point click costs no round trip. That is true of
 * the tile *file* and **not automatically true of the loaded batch**: deepscatter downloads a
 * tile's columns lazily, and a column no channel references is never in `Tile.record_batch`.
 * Measured at P9.4 — a clicked datum came back as `['ix', 'split', 'x', 'y']`, with `row` and
 * `sequence` simply absent, and the pane rendered "row undefined".
 *
 * The fix is to **ask for them up front**, which `prefetch` does. It costs no extra network:
 * deepscatter registers a transformation per root-tile field that reads from `get_arrow(null)`,
 * and that call is memoised per tile — so pulling `sequence` in decodes a buffer the browser has
 * already fetched for `x` and `y`.
 */
const IDENTITY_COLUMNS = ['x', 'y', 'ix', 'row', 'sequence'];

/**
 * The `split` channel's fixed order, matching `build.SPLIT_NAMES` (`§5`: 0 train, 1 val, 2 test).
 *
 * **Pinned rather than derived, because two atlases are compared side by side** (`V17`). A palette
 * assigned in first-seen order would give `train` different colours in the two panes, and the
 * difference would read as a property of the models.
 */
const SPLIT_DOMAIN: [string, string, ...string[]] = ['train', 'val', 'test'];
const SPLIT_RANGE = ['#4c78a8', '#c25c07', '#54a24b'];

/**
 * Ten categorical colours, ours rather than deepscatter's.
 *
 * `range: 'category10'` is a 2.x spelling: `CategoricalColorScale.range` is `Colorname[]` in 3.x
 * and a string there is a type error — one of the four hazards above, caught by `tsc` rather than
 * at runtime because this module is TypeScript.
 */
/*
 * **Five of the ten were darkened at P14.9 (`V77`) when the page went white**, and only five: a
 * mark on the map has to clear 3.0 contrast against its background to be a mark at all, and the
 * originals were picked against `#111318`. Measured on white before and after:
 *
 *   orange #f58518 -> #c25c07   2.55 -> 4.35        blue   #4c78a8  4.61  kept
 *   teal   #72b7b2 -> #3f8a85   2.30 -> 4.05        green  #54a24b  3.16  kept
 *   yellow #eeca3b -> #9a7d10   1.60 -> 3.95        red    #e45756  3.62  kept
 *   pink   #ff9da6 -> #c2566a   1.98 -> 4.35        purple #b279a2  3.41  kept
 *   grey   #bab0ac -> #7d746f   2.12 -> 4.57        brown  #9d755d  4.09  kept
 *
 * Each is darkened *within its own hue* rather than replaced, so a reader who has learned the
 * palette still reads the same ten categories. The minimum went 1.60 -> 3.16, and every colour
 * stays at or above 4.03 on the old dark background -- so this is an improvement there too, and
 * nothing here would have to be undone if the theme were ever reverted.
 *
 * The **continuous** channel is untouched and that was also measured: viridis has the highest
 * minimum contrast on white (1.26) of any perceptually-uniform ramp, and every sequential
 * alternative is worse (Blues 1.04, PuBuGn 1.05, Magma 1.05). See `V77` for why truncating it was
 * rejected.
 *
 * `src/peptide_vis/plots.py` transcribes both lists and a test compares them (`V68`).
 */
const CATEGORY_RANGE = [
  '#4c78a8', '#c25c07', '#54a24b', '#e45756', '#3f8a85',
  '#9a7d10', '#b279a2', '#c2566a', '#9d755d', '#7d746f',
];

/**
 * The map's background, read from the stylesheet rather than restated here (`V76`, P14.8).
 *
 * `app.css`'s `--bg` is the page *and* the canvas deepscatter paints under its points, and the
 * two were two literals that would drift the first time either was edited -- a dark map inside a
 * white page, with nothing to catch it.
 *
 * **The fallback is a failure mode, not a second palette.** An empty token means no stylesheet,
 * and `ctx.fillStyle = ''` is ignored by the canvas, which then keeps its default of transparent
 * black -- a black map. Returning white instead fails visibly in the right direction.
 *
 * **deepscatter reads this once, at construction** (`deepscatter.js:28249`, a `fillRect` on a
 * separate 2D canvas under the WebGL one). It cannot be changed through `plotAPI`, which is what
 * makes a runtime theme toggle expensive.
 */
function backgroundColour(): string {
  const token = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  return token === '' ? '#ffffff' : token;
}

/**
 * The fallback for `--flat`, and the value that token carries.
 *
 * Kept in step with `app.css` by hand, which is why `flatColour` reads the stylesheet rather than
 * using this: it applies only when there is no stylesheet at all.
 */
const FLAT_FALLBACK = '#a9a29d';

/**
 * The one colour the corpus takes under the select's `none` (`V154`, P31).
 *
 * **Read from the stylesheet rather than restated here**, for `backgroundColour`'s reason (`V76`):
 * the curated glyphs are styled in `app.css` and were chosen to be distinguishable against *this*
 * colour, so a literal here would be a second source of truth that drifts the first time either
 * is edited — and the symptom would be a legible map that quietly stops being legible.
 *
 * Unlike `backgroundColour`, this one is re-read on every call: it goes through `plotAPI`, which
 * can be called again, where the background is fixed at construction.
 */
function flatColour(): string {
  const token = getComputedStyle(document.documentElement).getPropertyValue('--flat').trim();
  return token === '' ? FLAT_FALLBACK : token;
}

/**
 * `plotAPI`'s argument, widened by the one field the runtime requires and the declaration omits.
 *
 * The intersection is what keeps the rest typechecked: everything except `source_url` is still
 * validated against `APICall`, so a wrong channel shape is still a compile error — which is how
 * `{range: 'category10', domain: [-2047, 2047]}` was caught at P6.5 before it ever ran.
 */
type InitialCall = Parameters<Scatterplot['plotAPI']>[0] & { source_url: string };

type Encoding = NonNullable<Parameters<Scatterplot['plotAPI']>[0]['encoding']>;

/** What `plotAPI`'s `{ deeptable }` form takes. Opaque here — only `Scatterplot` reads inside it. */
type DeeptableHandle = Parameters<Scatterplot['load_deeptable']>[0]['deeptable'];

function toDatum(row: StructRowProxy): PointDatum {
  const raw = row as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    out[key] = typeof value === 'bigint' ? Number(value) : value;
  }
  return out as unknown as PointDatum;
}

class DeepscatterRenderer implements LatentRenderer {
  private plot: Scatterplot;
  private pointHandlers: ((d: PointDatum) => void)[] = [];
  private emptyHandlers: ((xy: [number, number]) => void)[] = [];
  private colour: { column: string; role: ColumnRole } | null = null;
  private detachClicks: (() => void) | null = null;
  private loaded = false;

  /**
   * `width`/`height` are **not optional in practice**, and finding that out cost a measurement.
   *
   * `Scatterplot`'s constructor is `this.width = width || window.innerWidth`, and `bind` sets the
   * canvas and SVG `width`/`height` attributes from the same expression. So a plot in a
   * half-viewport pane draws at *full window width* unless it is told otherwise: the canvas
   * overflows its box, and the hit test — which is a `color_pick` in canvas pixels — disagrees
   * with what the reader sees by exactly the overflow. There is no `ResizeObserver` on the plot
   * either, so this is set once, at construction, and a pane that changes size is rebuilt rather
   * than resized (P12.1).
   */
  constructor(
    private readonly selector: string,
    width?: number,
    height?: number,
  ) {
    this.plot = new Scatterplot(selector, width, height);
  }

  /**
   * Point the plot at a tile directory. `source_url` goes in the **first `plotAPI` call**.
   *
   * The `Scatterplot` constructor also accepts `source_url` and calls `void
   * this.load_deeptable({source_url})` — unawaited. So the constructor route typechecks and then
   * loses the race: `plotAPI` runs before `_root` is set and throws *"The initial API call specify
   * exactly one of source_url, arrow_table, or arrow_buffer"* anyway. Verified in a browser at
   * P6.7, which is the only place it could have been (`V52`).
   */
  /**
   * A `Deeptable` per view — a tile directory, or a raw-dimension pair — so switching back to one
   * already loaded costs nothing (P10.5, P11.3).
   *
   * `plotAPI`'s `DataSpec` accepts `{ deeptable }` as one of its four forms, which is what makes
   * this a cache rather than a re-fetch: the quadtree, every downloaded tile and every column
   * pulled in by `prefetch` survive the switch. **Bounded by the manifest for tiled views — two
   * here — and by the viewer for raw ones**, which are `C(D, 2)` pairs, so a long session flicking
   * through dimension pairs grows this without limit. Each entry is a rendered subsample rather
   * than a corpus, so at `RENDER_BUDGET` points it is single-digit MB; if that ever bites, this is
   * where an LRU goes, mirroring `Atlas.raw_projection`'s. Cleared by `destroy`.
   */
  private readonly deeptables = new Map<string, DeeptableHandle>();

  /** Point the plot at a tile directory, loading it or reusing one already loaded (P10.5). */
  async setDeeptable(sourceUrl: string, columns: string[] = []): Promise<void> {
    this.wanted = [...new Set([...IDENTITY_COLUMNS, ...columns])];
    await this.load(sourceUrl, { source_url: sourceUrl });
  }

  /**
   * The untiled path: one Arrow IPC buffer, already whole (`V12`, P11.3).
   *
   * deepscatter's `wrapArrowTable` turns each record batch into a pseudo-tile and synthesises a
   * manifest from the `ix` column — so the same `visible_tiles()` walk that `nearestRendered`,
   * `categoriesOf` and `rowsWhereTrue` use over real tiles works here unchanged. That is what
   * makes a raw-dimension view the *same* map rather than a second renderer.
   *
   * `wanted` is reset to the identity columns because there is nothing to ask for: every column
   * the server sent is in the batch. `prefetch` still runs on zoom and finds nothing to do.
   */
  async setArrowBuffer(key: string, buffer: Uint8Array): Promise<void> {
    this.wanted = [...IDENTITY_COLUMNS];
    await this.load(key, { arrow_buffer: buffer });
  }

  /**
   * Load or reuse one view, then apply every channel that was configured for it.
   *
   * `key` is the cache identity — a tile URL or a `raw:<i>,<j>` name — and `spec` is how to build
   * it the first time. Everything after the load is common to both paths, which is why they are
   * one method: a second copy of the `plotAPI` call is a second place for `max_points` and the
   * tooltip to drift.
   *
   * **`plotAPI` cannot switch the data source, and its declaration does not say so.** It reads
   * `source_url` **only when `_root` is undefined** — that is, on the very first call. Passing a
   * new one afterwards is silently ignored and the previous projection stays on screen, which is a
   * wrong map with no error anywhere. Every switch therefore goes through `load_deeptable`, which
   * is the method that actually assigns `_root`.
   *
   * **And the `Deeptable` must not travel through `plotAPI`'s prefs.** `plotAPI` merges its
   * argument into `this.prefs`, which is serialised on the render path; a `Deeptable` holds
   * `_children` cycles, so it throws `Converting circular structure to JSON` once per frame —
   * 242 of them in one measured switch, with the map still drawing. `load_deeptable({deeptable})`
   * is the supported way in, and it keeps the object out of `prefs` entirely. Both found at
   * P10.5; see `V63`.
   */
  private async load(key: string, spec: Record<string, unknown>): Promise<void> {
    const cached = this.deeptables.get(key);
    await this.plot.load_deeptable(
      (cached === undefined ? spec : { deeptable: cached }) as Parameters<
        Scatterplot['load_deeptable']
      >[0],
    );
    this.deeptables.set(key, this.plot.deeptable as unknown as DeeptableHandle);
    this.rebindView();

    const call: InitialCall = {
      // Still passed, and still required: on the **first** call `plotAPI` re-checks `_root`, and
      // an absent data source there is the `"specify exactly one of …"` throw (`V52`). The cast
      // below is what lets the untiled path satisfy it with `arrow_buffer` instead.
      ...(spec as { source_url: string }),
      max_points: RENDER_BUDGET,
      point_size: 2,
      alpha: 12,
      background_color: backgroundColour(),
      encoding: {
        x: { field: 'x', transform: 'literal' },
        y: { field: 'y', transform: 'literal' },
      },
      // A **real function**, not a string lambda (`plan §7.4`). Free, for the same reason the
      // click is: `row` and `sequence` are already in the loaded batch (`V13` + `prefetch`).
      // Deliberately terse — the detail pane is where a point is read, and a tooltip that
      // repeated it would be a second place for the two to disagree.
      tooltip_html: (datum) => {
        const raw = datum as unknown as Record<string, unknown>;
        return raw['sequence'] === undefined
          ? '…'
          : `row ${String(raw['row'])}<br>${String(raw['sequence'])}`;
      },
    };
    await this.plot.plotAPI(call);
    this.loaded = true;
    this.attachClicks();
    await this.prefetch();
    // **Again on every zoom**, because a tile that arrives later arrives with the channel columns
    // only. Without this, panning into new territory would produce points that are drawn and not
    // yet clickable — and the honest handling of that is below, in `pickAt`.
    this.plot.on_zoom = () => {
      void this.prefetch();
      // The overlays are in screen space, so they have to follow every pan and zoom.
      this.placeLandmarks();
      this.place(this.pins);
      this.place(this.marks);
      this.placeCurated();
      return null;
    };
    // Re-apply whatever was asked for before the tiles existed, so the caller can configure the
    // channels once and switch projections without restating them.
    if (this.colour !== null) this.setColorChannel(this.colour.column, this.colour.role);
    if (this.landmarkColumn !== null) this.setHighlight(this.landmarkColumn);
    if (this.filter !== null) this.setFilter(this.filter.column, this.filter.values);
  }

  /**
   * Re-point every cache that was pinned to the **first** deeptable this plot ever loaded
   * (`V121`).
   *
   * **`plotAPI` calls `reinitialize()` only on the very first call**, and `reinitialize` is the
   * only place that runs `attach_tiles` and builds a fresh `Zoom`:
   *
   *     if (this._zoom === undefined || this._renderer === undefined) await this.reinitialize();
   *
   * So on every switch after the first, two things stay pinned to the **first** projection:
   * `Zoom.deeptable`, a plain field assigned once; and `Zoom._scales`, which `scales()` computes
   * from `this.deeptable.extent` and then **caches with no invalidation**:
   *
   *     scales() { if (this._scales) { ...rescale by transform...; return this._scales } ... }
   *
   * `ReglRenderer.deeptable` is a *getter* onto the scatterplot, so the new points are found and
   * drawn — through the old projection's linear scale. Measured on `thanatin_seeded` after
   * `pca_0_1 → umap`: `deeptable.extent.x` was UMAP's `[-6.98, 20.96]` while
   * `_zoom._scales.x.domain()` was still PCA's `[-0.659, 0.653]`, a factor of 21. The map drew a
   * thin diagonal streak of the embedding's corner, `color_pick` found nothing at 15 x 11 sampled
   * pixels, and all eight landmarks were placed past 11,000 px on a 1,200 px canvas and hidden.
   *
   * **That is the whole of the "the seed label disappears when I switch projection" report**, in
   * both rounds — the labels are just the part of it a reader can name. `V111` and `V115` were
   * both treating symptoms in the overlay; the map underneath was wrong too.
   *
   * **Direction-dependent, which is why it looked like it only broke one way.** Whichever
   * projection has the larger extent is unusable when reached from the smaller one, and the other
   * direction merely squeezes everything towards the centre, which reads as a bad-but-plausible
   * map rather than as a fault.
   *
   * Both fields are declared public in `interaction.d.ts` (`deeptable?: Deeptable`,
   * `_scales?: ScaleSet`, `attach_tiles(tiles): this`), so this is reaching for a supported API
   * rather than into a private. It is a fifth entry for this module's list of places the library
   * behaves differently from how a reader of its declarations would expect.
   *
   * **The reader's pan is deliberately kept.** `Zoom.transform` is a d3 transform in *screen*
   * pixels, so re-deriving the base scale from the new extent and reapplying the same transform
   * preserves "how far in am I" across a switch. Resetting it would be a second decision, and one
   * the reader did not ask for.
   */
  private rebindView(): void {
    const deeptable = this.plot.deeptable;
    const zoom = this.plot._zoom;
    const renderer = this.plot._renderer;
    // Nothing to rebind on the first load: `plotAPI` has not run `reinitialize` yet, and it will.
    if (zoom === undefined || renderer === undefined) return;

    // The DOM half — what `placeLandmarks` and `placePins` read.
    zoom.attach_tiles(deeptable);
    zoom._scales = undefined;

    // The GPU half. `_webgl_scale_history` is the uniform the vertex shader positions by, and it
    // is written **once**, inside the `ReglRenderer` constructor's `_initializations` promise,
    // from a `default_webgl_scale` that caches into `_default_webgl_scale`. Nothing invalidates
    // either. Clearing the cache and re-reading the getter is what recomputes it, in that order —
    // `default_webgl_scale` calls `zoom.webgl_scale()`, so the two lines above have to come first.
    //
    // **Assigned rather than cleared**: `tick()` opens with
    // `if (this._webgl_scale_history === undefined) return`, so leaving it undefined stops the
    // render loop instead of refreshing it. Both frames are set to the new scale because the
    // shader interpolates between them for zoom animation, and interpolating from the previous
    // projection's scale would animate through nonsense.
    renderer.deeptable = deeptable;
    renderer._default_webgl_scale = undefined;
    const scale = renderer.default_webgl_scale;
    renderer._webgl_scale_history = [scale, scale];
  }

  private wanted: string[] = IDENTITY_COLUMNS;

  /**
   * Pull `wanted` into every tile down to the current render depth.
   *
   * Idempotent by construction: `spawnDownloads` scores only tiles that are missing a field, and
   * the underlying `get_arrow(null)` is memoised per tile. Failures are swallowed rather than
   * thrown — a prefetch that loses a race with a tile eviction must not take the map down, and
   * the click path already handles a datum that is not ready.
   */
  private async prefetch(): Promise<void> {
    const renderer = this.plot._renderer;
    if (renderer === undefined) return;
    try {
      await this.plot.deeptable.downloadFieldsToDepth(renderer.max_ix, this.wanted);
    } catch {
      // Left deliberately silent; see above.
    }
  }

  /** Whether a datum carries everything a click needs to be answered without a fetch. */
  private isReady(row: Record<string, unknown>): boolean {
    return row['row'] !== undefined && row['sequence'] !== undefined;
  }

  private domainOf(column: string): [number, number] {
    try {
      return this.plot.deeptable.domain(column);
    } catch {
      // A column the loaded tiles have not populated yet. A neutral domain keeps the channel
      // change from failing outright; the next call, once tiles are in, gets the real one.
      return [0, 1];
    }
  }

  /**
   * The distinct values of a categorical column, read out of the **loaded tiles**.
   *
   * Deriving them rather than reading `manifest.columns` is deliberate: the manifest records a
   * column's *type* and *role*, never its values, and `seed_id` has 8 of them in this corpus and
   * would have thousands in another. Sorted so the assignment is stable across reloads.
   */
  private categoriesOf(column: string): [string, string, ...string[]] {
    const seen = new Set<string>();
    const renderer = this.plot._renderer;
    if (renderer !== undefined) {
      for (const tile of renderer.visible_tiles()) {
        if (!tile.hasLoadedColumn(column)) continue;
        const values = (tile.record_batch as RecordBatch).getChild(column);
        if (values === null) continue;
        for (let i = 0; i < values.length && seen.size < CATEGORY_RANGE.length; i++) {
          const v = values.get(i);
          if (v !== null && v !== undefined) seen.add(String(v));
        }
      }
    }
    const sorted = [...seen].sort();
    // A domain needs at least two entries to be a `CategoricalColorScale`; pad rather than throw,
    // because a single-category column is a legitimate corpus, not an error.
    while (sorted.length < 2) sorted.push(`__unused_${sorted.length}`);
    return sorted as [string, string, ...string[]];
  }

  /**
   * Landmark overlay (`V18`): draw these **atlas rows** in the foreground.
   *
   * §8 named this parameter `ix`, before `V51` split the two indices. It takes `row` because that
   * is what a caller has — `is_seed` is a column of the atlas, addressed by row.
   */
  /**
   * Draw the rows where `column` is true as **labelled landmarks over the canvas** (P10.2, `V18`).
   *
   * **Not a deepscatter channel, and that was not the first choice.** The obvious implementation
   * is `encoding.foreground` and `encoding.size` with a lambda over `is_seed`. deepscatter builds
   * a scale for the channel's field and throws `Domain expected to be 'number', but was boolean`;
   * `add_labels` then failed separately with a circular-structure error and rendered no text.
   * Measured at P10.2 — see `V63`.
   *
   * A DOM overlay satisfies all four halves of the requirement instead, and each of them cleanly:
   * larger than a point, **labelled** with real text, repositioned on every zoom so they stay put
   * at any magnification, and **clickable** — a landmark click goes through the same
   * `onPointClick` handlers as any other point, so interaction mode still decides what it means.
   */
  /**
   * Colour by one column, dispatching on its **declared role** rather than on its name (`V18`).
   *
   * That is what makes a future label — a measured MIC, a predicted property — a new column in the
   * manifest rather than a new branch here. The one name-keyed case is `split`, whose three-way
   * order is pinned by §5 and whose colours must not be re-shuffled between two atlases being
   * compared side by side (`V17`).
   */
  setColorChannel(column: string, role: ColumnRole): void {
    if (role === 'index' || role === 'boolean') {
      // Neither is a palette. `index` is an address, and a `boolean` column is `V18`'s **landmark
      // overlay** — `setHighlight` is where it belongs, and deepscatter's `ColorScaleChannel`
      // cannot express a two-value boolean domain anyway (its categorical domain is `string[]`).
      return;
    }
    this.colour = { column, role };
    if (!this.loaded) return;

    let color: Encoding['color'];
    if (role === 'continuous') {
      color = { field: column, range: 'viridis', domain: this.domainOf(column) };
    } else if (column === 'split') {
      color = { field: column, domain: SPLIT_DOMAIN, range: SPLIT_RANGE };
    } else {
      color = { field: column, domain: this.categoriesOf(column), range: CATEGORY_RANGE };
    }
    void this.plot.plotAPI({ encoding: { color } } as InitialCall);
  }

  clearColorChannel(): void {
    this.colour = null;
    if (!this.loaded) return;
    // `{ constant }` rather than `null`. Passing `null` removes the channel and deepscatter falls
    // back to its *own* default colour, which is not one of ours and is not guaranteed to clear
    // the 3.0 contrast bar `V77` set against `--bg`. Naming the colour keeps the flat map inside
    // the palette the rest of the page uses.
    const color: Encoding['color'] = { constant: flatColour() };
    void this.plot.plotAPI({ encoding: { color } } as InitialCall);
  }

  setHighlight(column: string | null): void {
    this.landmarkColumn = column;
    if (!this.loaded) return;
    this.landmarkRows = column === null ? [] : this.rowsWhereTrue(column);
    this.drawLandmarks();
    // **Every time, not only on a miss** — see `settleLandmarks` (`V111`, `V115`).
    if (column !== null) this.settleLandmarks(LANDMARK_SETTLE_TICKS);
  }

  /**
   * Re-scan and re-place the landmarks for a bounded window after a view changes (`V111`, `V115`).
   *
   * **Two different things go wrong here and they need the same window**, which is why this is one
   * method rather than two.
   *
   * 1. **The rows are not there yet.** `rowsWhereTrue` reads the tiles loaded *now*, and after a
   *    switch `visible_tiles()` has not been repopulated by the render loop. The scan finds
   *    nothing, and nothing ever asks again: `on_zoom` only *places* marks it already has. This is
   *    the case `V111` fixed and it is real on a first, uncached load.
   * 2. **The rows are right and the transform is stale**, which is what `V111` missed and what
   *    left UMAP → PCA still broken after it (`V115`). `landmarks()` returned the correct PCA
   *    coordinates — `app.browser.test.ts` asserts exactly that and it passed — but they were
   *    converted to screen pixels through the *previous* projection's `Zoom.scales()`, landed far
   *    outside the canvas, and `placeLandmarks` set `hidden` on all eight. Toggling the selector
   *    worked because by then the zoom had settled.
   *
   * So every tick re-places, and re-scans only while the set is empty. Re-placing eight
   * `transform` strings costs nothing; the scan is the expensive half and stays conditional.
   *
   * **Bounded, not a poll.** A boolean column with no true rows anywhere is a legitimate atlas —
   * the residue atlases have several — so this must cost a fixed number of scans rather than one
   * per frame forever, which is also why it is not folded into `on_zoom`.
   *
   * **And when the window closes with nothing found, `huntLandmarks` takes over** (`V149`). The
   * two halves are separated there because only *one* of the two causes above outlives 3 s: a
   * stale transform settles in that time by construction, while tiles arriving over a network do
   * not have to.
   */
  private settleLandmarks(ticks: number): void {
    if (this.landmarkRetry !== null) clearTimeout(this.landmarkRetry);
    this.landmarkRetry = null;
    if (this.landmarkColumn === null) return;
    if (ticks <= 0) {
      // Nothing to hunt for when the rows are already in hand; the placement half is done too.
      if (this.landmarkRows.length === 0) this.huntLandmarks(LANDMARK_HUNT_FIRST_MS);
      return;
    }
    this.landmarkRetry = setTimeout(() => {
      this.landmarkRetry = null;
      const column = this.landmarkColumn;
      if (!this.loaded || column === null) return;
      if (this.landmarkRows.length === 0) this.landmarkRows = this.rowsWhereTrue(column);
      this.drawLandmarks();
      this.settleLandmarks(ticks - 1);
    }, LANDMARK_SETTLE_MS);
  }

  /**
   * Keep looking for the rows after the placement window has closed, **while none have been
   * found** (`V149`).
   *
   * The reader's report: a first load of the PCA map showed no seed landmark, and touching the
   * selector — which calls `setHighlight` again — brought it back. That is the shape of a search
   * that gave up: `rowsWhereTrue` reads the tiles loaded *now*, `on_zoom` only re-places rows it
   * already has, and nothing else asks again.
   *
   * **Only the scan, never the placement**, which is why this is not simply a longer
   * `settleLandmarks`. Re-placing exists for a transform that is briefly stale, and that resolves
   * within the 3 s window by construction; running it for another half a minute would be work
   * against a settled zoom. Once a scan succeeds this draws once and stops — the overlay then
   * follows the view through `on_zoom` like any other.
   *
   * It shares `landmarkRetry` with `settleLandmarks`, so the two can never be armed at once and
   * `setHighlight` cancels whichever is running.
   */
  private huntLandmarks(delay: number): void {
    if (this.landmarkRetry !== null) clearTimeout(this.landmarkRetry);
    this.landmarkRetry = null;
    if (delay > LANDMARK_HUNT_MAX_MS || this.landmarkColumn === null) return;
    this.landmarkRetry = setTimeout(() => {
      this.landmarkRetry = null;
      const column = this.landmarkColumn;
      if (!this.loaded || column === null || this.landmarkRows.length > 0) return;
      this.landmarkRows = this.rowsWhereTrue(column);
      if (this.landmarkRows.length > 0) {
        this.drawLandmarks();
        return;
      }
      this.huntLandmarks(delay * 2);
    }, delay);
  }

  setFilter(column: string | null, values: readonly unknown[] = []): void {
    this.filter = column === null ? null : { column, values };
    if (!this.loaded) return;
    const wanted = new Set(values.map(String));
    // A **real function**, not a string lambda: 3.x removed the `eval`-based form (`plan §7.4`).
    // `split` is a dictionary-encoded string column, so the lambda compares strings — the same
    // three names `§5` pins, never the underlying codes.
    const filter: Encoding['filter'] =
      column === null || wanted.size === 0
        ? { constant: true }
        : { field: column, lambda: (v) => wanted.has(String(v)) };
    void this.plot.plotAPI({ encoding: { filter } } as InitialCall);
  }

  landmarks(): PointDatum[] {
    return [...this.landmarkRows];
  }

  /**
   * The pinned cards (`V112`, `V116`, `P19.6`, `P19.10`).
   *
   * **Its own overlay, not the landmark one.** `drawLandmarks` calls `replaceChildren` on that
   * container whenever the landmark set changes, which would delete every pinned card on the next
   * projection switch or landmark toggle. Two containers is the whole of the fix and it needs no
   * coordination between them.
   *
   * The container is `pointer-events: none` like the landmark overlay, and the cards take them
   * back — so the map underneath stays draggable everywhere except over a card, which has to be
   * hoverable for the residue structures to be reachable from it.
   *
   * **A `Map` keyed by row, not a list** (`V116`). Two clicks on the same point must not leave two
   * cards on top of each other, and `unpin(row)` is what a card's own close control calls — both
   * are the identity question, and `V51` makes `row` the answer to it.
   */
  pin(datum: PointDatum, node: HTMLElement): void {
    const host = document.querySelector(this.selector) as HTMLElement | null;
    if (host === null) return;
    if (this.pinOverlay === null) {
      this.pinOverlay = document.createElement('div');
      this.pinOverlay.className = 'landmark-overlay pin-overlay';
      host.append(this.pinOverlay);
    }
    const stale = this.pins.get(datum.row);
    const width = this.keptWidth(stale);
    stale?.detachDrag?.();
    stale?.node.remove();
    this.pinOverlay.append(node);
    // **The drag position survives a re-pin, exactly as it does for a mark** (`V131`). Until Phase
    // 27 no caller could re-pin a live row — `selectPoint` pinned only `if (!pins.has(row))` — so
    // this was unreachable rather than right. A trajectory change rebuilds every pinned card, and
    // without the offset carried forward, adding a sixth point would yank the five cards the reader
    // had arranged back to their resting positions, undoing `V128`, `V129` and `V130` in one click.
    const pinned = placed([datum.x, datum.y], node, stale?.offset ?? { x: 0, y: 0 }, width);
    // **And the width the reader dragged the card to survives with it** (`V135`). Same failure as
    // the offset above and one step further along: `resize: horizontal` is written by the user agent
    // onto the node, so a rebuild threw it away and a card widened to fit a 16-mer re-wrapped on the
    // next mode switch.
    this.applyWidth(pinned);
    this.pins.set(datum.row, pinned);
    this.makeDraggable(pinned);
    this.place(this.pins);
  }

  unpin(row: number): void {
    const entry = this.pins.get(row);
    entry?.detachDrag?.();
    entry?.node.remove();
    this.pins.delete(row);
  }

  clearPins(): void {
    for (const entry of this.pins.values()) {
      entry.detachDrag?.();
      entry.node.remove();
    }
    this.pins.clear();
  }

  pinnedRows(): number[] {
    return [...this.pins.keys()];
  }

  /**
   * A decoded point's node, at a coordinate no corpus row occupies (`V123`, P20.2).
   *
   * **A third overlay, and each of the three exists for its own reason.** The landmark container
   * has `replaceChildren` called on it whenever the landmark set changes, so nothing durable can
   * live there. The pin container is emptied by `clearPins()` on every projection change, which is
   * right for a card that names a corpus row and wrong for a mark that the caller re-filters and
   * re-draws for the new view.
   *
   * Placement is the whole of this method's job. What the node says, whether it carries a card,
   * and what a click on it means are all `main.ts`'s — the same split `pin` keeps (§8).
   */
  mark(id: string, xy: [number, number], node: HTMLElement): void {
    const host = document.querySelector(this.selector) as HTMLElement | null;
    if (host === null) return;
    if (this.markOverlay === null) {
      this.markOverlay = document.createElement('div');
      this.markOverlay.className = 'landmark-overlay decoded-overlay';
      host.append(this.markOverlay);
    }
    const previous = this.marks.get(id);
    const width = this.keptWidth(previous);
    previous?.detachDrag?.();
    previous?.node.remove();
    this.markOverlay.append(node);
    // The drag position survives the card being closed and reopened: `main.ts` rebuilds the node
    // for that, and an arrangement the reader made should not be undone by reading one card.
    //
    // **And so does the width** (`V135`). This is the case that makes `Placed.width` worth keeping
    // rather than reading the old node's inline style and nothing else: closing the card destroys
    // the `.decoded-point` child that carried the width, so between the close and the reopen there
    // is no element left to read it from.
    const entry = placed(xy, node, previous?.offset ?? { x: 0, y: 0 }, width);
    this.applyWidth(entry);
    this.marks.set(id, entry);
    this.makeDraggable(entry);
    this.place(this.marks);
    // **Marks arrive at load time, and the landmarks proved that is the hard case** (`V115`).
    // A node placed before the zoom has settled on the new projection is converted through the
    // previous one's scale, lands far off canvas and is hidden. `pin` never meets this because a
    // pin follows a click, which is long after the switch; a restored mark is drawn immediately
    // after `showView`. Bounded re-placement, for the same reason `settleLandmarks` is bounded.
    this.settleMarks(MARK_SETTLE_TICKS);
  }

  unmark(id: string): void {
    const entry = this.marks.get(id);
    entry?.detachDrag?.();
    entry?.node.remove();
    this.marks.delete(id);
  }

  clearMarks(): void {
    for (const entry of this.marks.values()) {
      entry.detachDrag?.();
      entry.node.remove();
    }
    this.marks.clear();
  }

  markIds(): string[] {
    return [...this.marks.keys()];
  }

  /**
   * Screen positions for one overlay's nodes, on the same terms as `placeLandmarks` (`V112`).
   *
   * One routine over both groups: pinned cards and decoded marks differ in what they say and in
   * when they are cleared, and in nothing at all about where they go.
   */
  private place(entries: ReadonlyMap<unknown, Placed>): void {
    const zoom = this.plot._zoom;
    if (entries.size === 0 || zoom === undefined) return;
    let scales;
    try {
      scales = zoom.scales();
    } catch {
      return;
    }

    // **Every layout read before any style write.** `offsetWidth` forces a reflow if the previous
    // iteration has already written a `transform`, so an interleaved loop is one reflow per node
    // on every frame of a pan. Reading the whole set first costs exactly one.
    for (const entry of entries.values()) {
      this.measure(entry);
      this.measureRest(entry);
    }

    const glyphSize = markSizePx(
      zoom.transform?.k ?? 1,
      this.zoomBalance(),
      this.pointSize(),
      globalThis.devicePixelRatio || 1,
    );

    for (const entry of entries.values()) {
      const { xy, node, extent } = entry;
      const px = scales.x_(xy[0]);
      const py = scales.y_(xy[1]);
      node.style.transform = `translate(${px}px, ${py}px)`;
      // The diamond is drawn at the size deepscatter draws a point; the card is not scaled at
      // all, because it holds text. Set here rather than in the stylesheet because only the zoom
      // knows `k`.
      node.style.setProperty('--mark-size', `${glyphSize}px`);
      // **Flip rather than overflow** (P21.1). `hidden` below tests the *anchor*, and a card is
      // ~392 px wide and ~78 px tall beyond it — so an anchor comfortably inside the canvas can
      // put the whole card outside it, which reads as "the click did nothing" while the counter
      // says otherwise. Measured: a pinned card is 384 px wide against a 448 px panel.
      this.applyOffset(entry);
      node.classList.toggle('flip-x', px + extent.width > zoom.width);
      node.classList.toggle('flip-y', py + extent.height > zoom.height);
      // Hidden rather than removed when it is panned off screen, so panning back brings it back
      // without the caller having to re-pin — the same rule the landmarks follow.
      node.hidden = !(px >= 0 && py >= 0 && px <= zoom.width && py <= zoom.height);
    }
  }

  /**
   * Cache how far a node reaches past its own anchor, **measured only while it is not flipped**.
   *
   * That condition is the whole of the method. Flipping moves the card to the other side of the
   * anchor, so a flipped node measures a *different* extent — one that no longer overflows — and
   * a placement loop that re-measured every frame would unflip it, overflow again, reflip, and
   * oscillate once per frame. Measuring only in the natural position gives a stable number, and
   * still picks up a card the reader has widened by dragging its corner (`V118`), because a card
   * that is not currently flipped is re-measured on the next pan.
   */
  private measure(entry: Placed): void {
    const { node } = entry;
    if (node.hidden) return;
    if (node.classList.contains('flip-x') || node.classList.contains('flip-y')) return;
    // The node's own box when it *is* the card (a pinned card), and its children's when it is a
    // zero-size anchor (a decoded mark). `offsetLeft` is pre-transform and includes the margin,
    // so both cases are measured in the anchor's own frame.
    let width = node.offsetLeft + node.offsetWidth;
    let height = node.offsetTop + node.offsetHeight;
    for (const child of node.children) {
      const el = child as HTMLElement;
      width = Math.max(width, node.offsetLeft + el.offsetLeft + el.offsetWidth);
      height = Math.max(height, node.offsetTop + el.offsetTop + el.offsetHeight);
    }
    if (width > 0 || height > 0) entry.extent = { width, height };
  }

  /**
   * Cache where the draggable card rests relative to its anchor, and how big it is (`V130`).
   *
   * Separate from `measure` and **not** skipped while flipped, which is the difference that
   * matters. `extent` must be the *unflipped* reach or the flip oscillates; the drag bound must be
   * the *current* position or a flipped card is bounded on the wrong side of its point.
   *
   * Read here, in `place`'s read phase, so a drag move costs no layout: the bound is recomputed
   * from the cached box on every pointer move and every frame of a zoom.
   */
  private measureRest(entry: Placed): void {
    const { node, handle } = entry;
    if (handle === null || node.hidden) return;
    const width = handle.offsetWidth;
    const height = handle.offsetHeight;
    if (width === 0 && height === 0) return;
    // A pinned card *is* the placed node and flips by translating itself, which no layout read
    // reports — so the flip has to be added back by hand. A decoded card is a child that flips by
    // swapping `left` for `right`, and `offsetLeft` reports that honestly.
    const own = handle === node;
    const flipX = own && node.classList.contains('flip-x') ? -(width + FLIP_CLEARANCE_PX) : 0;
    const flipY = own && node.classList.contains('flip-y') ? -(height + FLIP_CLEARANCE_PX) : 0;
    entry.rest = {
      x: node.offsetLeft + (own ? 0 : handle.offsetLeft) + flipX,
      y: node.offsetTop + (own ? 0 : handle.offsetTop) + flipY,
      width,
      height,
    };
  }

  /**
   * The two prefs that decide how big deepscatter draws a point.
   *
   * Read from the plot rather than copied, so the marker stays the size of a point if either is
   * ever set in the `plotAPI` call. `marks.ts` combines them; these only fetch.
   */
  private zoomBalance(): number {
    const balance = (this.plot.prefs as { zoom_balance?: number } | undefined)?.zoom_balance;
    return typeof balance === 'number' && Number.isFinite(balance) ? balance : DEFAULT_ZOOM_BALANCE;
  }

  private pointSize(): number {
    const size = (this.plot.prefs as { point_size?: number } | undefined)?.point_size;
    return typeof size === 'number' && Number.isFinite(size) && size > 0 ? size : DEFAULT_POINT_SIZE;
  }

  /**
   * Let the reader drag this entry's card, within a box that scales with the zoom (`V128`, P24).
   *
   * **The caller says which element moves, with `data-drag`.** The renderer must not know that one
   * node is a card and another is a diamond on an anchor — that is `§8`'s split, and it does real
   * work here rather than being a formality: for a pinned card the draggable element *is* the
   * placed node, while for a decoded mark it is a child, and moving the parent would drag the
   * diamond off the coordinate it exists to name.
   *
   * **The offset lives in CSS custom properties on the handle, not in the placed node's
   * `transform`.** `place` owns that transform and rewrites it on every frame of a pan, so an
   * offset folded into it would be overwritten; one folded into the stylesheet's own rules would
   * fight the flip. Two variables the stylesheet composes is the only arrangement where neither
   * party has to know about the other.
   *
   * Three presses that are **not** drags, each excluded for its own reason:
   *
   * | Press | Why it is not a drag |
   * |---|---|
   * | on a `button` | the close control, and a card that dismissed itself when nudged would be unusable |
   * | in the bottom-right corner | `V118`'s `resize: horizontal` grip is drawn there by the user agent and fires an ordinary `pointerdown` |
   * | shorter than `DRAG_THRESHOLD_PX` | the residue spans open a structure card on click (`P18.1`), and every card is covered in them |
   */
  /**
   * The element the caller marked `[data-drag]`, which is the card itself or a child of it.
   *
   * One lookup for three callers — `makeDraggable`, and the two halves of the resize carry-forward
   * — because it is also **the element `resize: horizontal` is set on** in both `app.css` rules.
   * That is not a coincidence: `DRAG_RESIZE_CORNER_PX` exists precisely because the user agent draws
   * its resize grip in the corner of this element and its grip fires an ordinary `pointerdown` here.
   */
  private handleOf(node: HTMLElement): HTMLElement | null {
    return (node.matches('[data-drag]') ? node : node.querySelector('[data-drag]')) as
      | HTMLElement
      | null;
  }

  /**
   * The width to carry into a replacement node: what the reader dragged the old card to (`V135`).
   *
   * The live DOM value wins, because a resize since this entry was created is only recorded there —
   * the user agent writes the inline width and notifies nobody. `entry.width` is the fallback for the
   * case where the element that held it no longer exists, which is a decoded card that was closed and
   * is being reopened.
   */
  private keptWidth(stale: Placed | undefined): string {
    const live = stale?.handle?.style.width ?? '';
    return live !== '' ? live : (stale?.width ?? '');
  }

  /**
   * Write the carried width onto the new node, **before anything measures it**.
   *
   * Order is load-bearing: `makeDraggable` measures the card to compute its drag bound (`V130`), and
   * `place` measures it again to position it. Applied after either of those, the first bound and the
   * first placement would be computed for a card of the default width and then be wrong by the
   * difference until the next zoom or drag.
   */
  private applyWidth(entry: Placed): void {
    if (entry.width === '') return;
    const handle = this.handleOf(entry.node);
    if (handle !== null) handle.style.width = entry.width;
  }

  private makeDraggable(entry: Placed): void {
    const node = entry.node;
    const handle = this.handleOf(node);
    if (handle === null) return;
    entry.handle = handle;
    // Measured once here as well as in every placement: the first `applyOffset` below happens
    // before any `place` has run for this entry, and an unmeasured card would be bounded as if it
    // had no body at all.
    this.measureRest(entry);
    this.applyOffset(entry);

    let pointer: number | null = null;
    let dragging = false;
    let from = { x: 0, y: 0 };
    let origin = { x: 0, y: 0 };

    const onDown = (event: PointerEvent): void => {
      if (pointer !== null || event.button !== 0) return;
      if ((event.target as HTMLElement | null)?.closest('button') != null) return;
      const box = handle.getBoundingClientRect();
      if (
        event.clientX > box.right - DRAG_RESIZE_CORNER_PX &&
        event.clientY > box.bottom - DRAG_RESIZE_CORNER_PX
      ) {
        return;
      }
      pointer = event.pointerId;
      dragging = false;
      origin = { x: event.clientX, y: event.clientY };
      // **Start this drag from where the card is drawn, not from where it is stored.** The offset
      // is kept unclamped so that zooming back in restores an arrangement, which means it can be
      // far outside the box; a drag that resumed from the stored value would spend its first few
      // hundred pixels moving a number nobody can see, and the card would sit still against the
      // wall it was already against. By arithmetic, not measurement: shoved 500 px right against
      // a wall at 52, the next drag moves nothing until it has travelled 448 px back.
      const allowed = this.bounds(entry);
      from = {
        x: clampDrag(entry.offset.x, allowed.x),
        y: clampDrag(entry.offset.y, allowed.y),
      };
      // **Capture, and stop the press reaching the map.** Without the capture a drag that leaves
      // the card stops receiving events; without the stop, d3-zoom pans the map underneath.
      handle.setPointerCapture?.(event.pointerId);
      event.stopPropagation();
    };

    const onMove = (event: PointerEvent): void => {
      if (pointer !== event.pointerId) return;
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      dragging = true;
      handle.classList.add('dragging');
      entry.offset = { x: from.x + dx, y: from.y + dy };
      this.applyOffset(entry);
      event.preventDefault();
    };

    const onUp = (event: PointerEvent): void => {
      if (pointer !== event.pointerId) return;
      pointer = null;
      handle.releasePointerCapture?.(event.pointerId);
      handle.classList.remove('dragging');
      if (!dragging) return;
      dragging = false;
      // **Swallow the click this release is about to produce.** A residue span under the pointer
      // would otherwise pin its structure card at the end of every drag that started on one, and
      // every card is covered in them.
      handle.addEventListener('click', (click) => click.stopPropagation(), {
        capture: true,
        once: true,
      });
    };

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
    handle.style.touchAction = 'none';
    entry.detachDrag = () => {
      handle.removeEventListener('pointerdown', onDown);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
  }

  /**
   * Write the drag offset, clamped so the card stays within reach of its point (`V128`, `V130`).
   *
   * Called on every placement as well as on every drag move, because the box scales with `k`: a
   * card dragged 400 px out at high zoom has to come back towards its point when the reader zooms
   * out, or it would sit nearer some other point than its own.
   *
   * **The bound is on the gap between the card and the point, not on the offset from where the
   * card happens to rest** (`V130`). Those are the same thing only for a card centred on its
   * anchor, and neither card is: both rest below and to the right by half their own body, so
   * `V128`'s symmetric clamp described a box around the card's top-left corner. The reader read it
   * off the screen as a drag box that was not centred on the point, and it was not.
   *
   * The two axes are computed apart because `rest` differs per axis and flips per axis.
   */
  private applyOffset(entry: Placed): void {
    const { handle } = entry;
    if (handle === null) return;
    const box = this.bounds(entry);
    handle.style.setProperty('--drag-x', `${clampDrag(entry.offset.x, box.x)}px`);
    handle.style.setProperty('--drag-y', `${clampDrag(entry.offset.y, box.y)}px`);
  }

  /**
   * The offsets this entry may be drawn at, per axis, at the current zoom (`V130`).
   *
   * Shared by the write and by the start of a drag, which is the point of having it: those two
   * have to agree about where the wall is or a drag begins somewhere the card is not.
   *
   * The fallback is the old symmetric box, and it is reached only before the first measurement —
   * a card whose size is unknown cannot be bounded by a gap to its edge.
   */
  private bounds(entry: Placed): { x: DragRange; y: DragRange } {
    const limit = dragLimitPx(this.plot._zoom?.transform?.k ?? 1);
    const fallback = { lo: -limit, hi: limit };
    const rest = entry.rest;
    return {
      x: rest === null ? fallback : dragRange(rest.x, rest.width, limit),
      y: rest === null ? fallback : dragRange(rest.y, rest.height, limit),
    };
  }

  /**
   * Re-place the marks for a bounded window after they are added (`V123`, P20.2).
   *
   * Case 2 of `settleLandmarks` and only case 2: there is nothing to re-scan, because the caller
   * supplied the coordinates. What can be wrong is the transform they were converted through.
   * Bounded rather than a poll, for the same reason.
   */
  private settleMarks(ticks: number): void {
    if (this.markRetry !== null) clearTimeout(this.markRetry);
    this.markRetry = null;
    if (ticks <= 0 || this.marks.size === 0) return;
    this.markRetry = setTimeout(() => {
      this.markRetry = null;
      this.place(this.marks);
      this.settleMarks(ticks - 1);
    }, LANDMARK_SETTLE_MS);
  }

  private readonly pins = new Map<number, Placed>();
  private pinOverlay: HTMLElement | null = null;
  private readonly marks = new Map<string, Placed>();
  private markOverlay: HTMLElement | null = null;
  /** The pending `settleMarks` timer, so a second batch cancels the first's. */
  private markRetry: ReturnType<typeof setTimeout> | null = null;

  // -- the curated overlay (`V153`, P31) -------------------------------------------------------

  private readonly curated = new Map<string, { xy: [number, number]; node: HTMLElement }>();
  private curatedOverlay: HTMLElement | null = null;
  private curatedRetry: ReturnType<typeof setTimeout> | null = null;

  setCuratedPoints(points: readonly CuratedPoint[]): void {
    const host = document.querySelector(this.selector) as HTMLElement | null;
    if (host === null) return;
    if (points.length === 0 && this.curated.size === 0) return;
    if (this.curatedOverlay === null) {
      this.curatedOverlay = document.createElement('div');
      // Shares `landmark-overlay`'s geometry — absolutely positioned, pointer-events off — and
      // adds its own class so the stylesheet can reach the curated glyphs alone.
      this.curatedOverlay.className = 'landmark-overlay curated-overlay';
      host.append(this.curatedOverlay);
    }

    // **Diff by id rather than rebuild.** Toggling one set on must not tear down and recreate the
    // other two's nodes: at 200 points that is 200 DOM insertions for a change that touched none
    // of them, and it would drop any hover the reader was on.
    const wanted = new Set(points.map((p) => p.id));
    for (const [id, entry] of this.curated) {
      if (!wanted.has(id)) {
        entry.node.remove();
        this.curated.delete(id);
      }
    }
    for (const point of points) {
      const existing = this.curated.get(point.id);
      const node = existing?.node ?? this.buildCuratedNode();
      // `className` is rewritten rather than toggled: the set a glyph belongs to can change when
      // precedence changes — a soonest point hidden from candidates and back — and enumerating
      // the classes to remove would be a second list to keep in step with `CURATED_ORDER`.
      node.className = `curated-glyph ${point.setClass}`;
      node.title = point.title;
      const text = node.firstElementChild as HTMLElement;
      text.textContent = point.label ?? '';
      text.hidden = point.label === null;
      if (existing === undefined) this.curatedOverlay.append(node);
      this.curated.set(point.id, { xy: point.xy, node });
    }
    this.placeCurated();
    // **The same load-time hazard the marks have** (`V115`): a point placed before the zoom has
    // settled on a new projection is converted through the previous one's scale and lands off
    // canvas. Bounded re-placement, not a poll.
    this.settleCurated(MARK_SETTLE_TICKS);
  }

  curatedIds(): string[] {
    return [...this.curated.keys()];
  }

  private buildCuratedNode(): HTMLElement {
    const node = document.createElement('div');
    node.className = 'curated-glyph';
    const text = document.createElement('span');
    text.className = 'curated-label';
    node.append(text);
    return node;
  }

  /**
   * Position every curated glyph. **No layout reads**, which is the whole reason this is not
   * `place`.
   *
   * `place` calls `measure` and `measureRest` per entry so a card can flip and be drag-bounded;
   * both read `offsetWidth`/`offsetLeft`. A curated glyph is a dot anchored at its point, so
   * neither question arises and the loop is pure style writes — which is what makes 200+ of them
   * affordable on every frame of a pan.
   */
  private placeCurated(): void {
    const zoom = this.plot._zoom;
    if (this.curated.size === 0 || zoom === undefined) return;
    let scales;
    try {
      scales = zoom.scales();
    } catch {
      return;
    }
    const glyphSize = markSizePx(
      zoom.transform?.k ?? 1,
      this.zoomBalance(),
      this.pointSize(),
      globalThis.devicePixelRatio || 1,
    );
    for (const { xy, node } of this.curated.values()) {
      const px = scales.x_(xy[0]);
      const py = scales.y_(xy[1]);
      node.style.transform = `translate(${px}px, ${py}px)`;
      node.style.setProperty('--mark-size', `${glyphSize}px`);
      // Hidden rather than removed off screen, so panning back does not rebuild the DOM — the
      // rule the landmarks and the marks both follow.
      node.hidden = !(px >= 0 && py >= 0 && px <= zoom.width && py <= zoom.height);
    }
  }

  private settleCurated(ticks: number): void {
    if (this.curatedRetry !== null) clearTimeout(this.curatedRetry);
    this.curatedRetry = null;
    if (ticks <= 0 || this.curated.size === 0) return;
    this.curatedRetry = setTimeout(() => {
      this.curatedRetry = null;
      this.placeCurated();
      this.settleCurated(ticks - 1);
    }, LANDMARK_SETTLE_MS);
  }

  private clearCurated(): void {
    for (const entry of this.curated.values()) entry.node.remove();
    this.curated.clear();
    if (this.curatedRetry !== null) clearTimeout(this.curatedRetry);
    this.curatedRetry = null;
  }

  /**
   * Build the overlay and place every landmark at its current screen position.
   *
   * Called after load, on every zoom, and whenever the landmark column changes. Points outside the
   * viewport are hidden rather than removed, so panning back does not rebuild the DOM.
   */
  private drawLandmarks(): void {
    const node = document.querySelector(this.selector) as HTMLElement | null;
    if (node === null) return;
    if (this.overlay === null) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'landmark-overlay';
      node.append(this.overlay);
    }
    if (this.landmarkRows.length === 0) {
      this.overlay.replaceChildren();
      return;
    }
    if (this.overlay.childElementCount !== this.landmarkRows.length) {
      this.overlay.replaceChildren(
        ...this.landmarkRows.map((row, at) => {
          const mark = document.createElement('button');
          mark.className = 'landmark';
          mark.type = 'button';
          // Written here as well as in `placeLandmarks`, because a button built before the zoom
          // exists is not placed on this pass and would carry no text until the next one.
          this.nameLandmark(mark, row);
          // Routed through the same handlers as a canvas click: the renderer reports *where* the
          // click landed and `main.ts` decides what it means (§8). A landmark that selected
          // directly would behave differently from the point underneath it.
          //
          // **The row is read at click time, never captured** (`V148`). The buttons are rebuilt
          // only when the *count* changes, and a projection switch changes neither the count nor
          // the row numbers — only the coordinates. A closure over the datum therefore survived a
          // switch and handed `main.ts` the previous projection's `x`/`y`, which pinned a card
          // through the new scale, off the canvas, and `hidden`. The label, the title and the
          // address are written by `placeLandmarks` for the same reason.
          mark.addEventListener('click', (event) => {
            event.stopPropagation();
            const live = this.landmarkRows[at];
            if (live === undefined) return;
            for (const cb of this.pointHandlers) cb(live);
          });
          return mark;
        }),
      );
    }
    this.placeLandmarks();
  }

  private placeLandmarks(): void {
    const zoom = this.plot._zoom;
    if (this.overlay === null || zoom === undefined) return;
    let scales;
    try {
      scales = zoom.scales();
    } catch {
      return;
    }
    const marks = this.overlay.children;
    for (let i = 0; i < marks.length && i < this.landmarkRows.length; i++) {
      const mark = marks[i] as HTMLElement;
      const row = this.landmarkRows[i];
      // **What this button says, written here rather than where it was created** (`V148`). A
      // button is reused whenever the new set is the same size, so its text would otherwise still
      // name whatever row sat at this position when the overlay was last rebuilt. Guarded on the
      // column and the row together, so the common case — a zoom, where neither moved — writes
      // nothing.
      this.nameLandmark(mark, row);
      const px = scales.x_(row.x);
      const py = scales.y_(row.y);
      const visible = px >= 0 && py >= 0 && px <= zoom.width && py <= zoom.height;
      mark.style.transform = `translate(${px}px, ${py}px)`;
      mark.hidden = !visible;
    }
  }

  /**
   * What one landmark button says and which row it addresses.
   *
   * Guarded on the column and the row together, so the common case — a zoom, where neither moved
   * — writes nothing, while a button reused for a different row is relabelled.
   */
  private nameLandmark(mark: HTMLElement, row: PointDatum): void {
    const named = `${this.landmarkColumn ?? ''}:${row.row}`;
    if (mark.dataset['named'] === named) return;
    mark.textContent = landmarkLabel(row, this.landmarkColumn);
    // The row is the address and must stay reachable even when the label is a name: the detail
    // panel keys on it and `V51` makes it the only identifier that means one thing.
    mark.title = `row ${row.row}`;
    mark.dataset['row'] = String(row.row);
    mark.dataset['named'] = named;
  }

  private rowsWhereTrue(column: string): PointDatum[] {
    const renderer = this.plot._renderer;
    if (renderer === undefined) return [];
    const found: PointDatum[] = [];
    const seen = new Set<number>();
    for (const tile of renderer.visible_tiles()) {
      if (!tile.hasLoadedColumn(column) || !IDENTITY_COLUMNS.every((c) => tile.hasLoadedColumn(c))) {
        continue;
      }
      const batch = tile.record_batch as RecordBatch;
      const flags = batch.getChild(column);
      if (flags === null) continue;
      for (let i = 0; i < batch.numRows; i++) {
        if (flags.get(i) !== true) continue;
        const datum = toDatum(batch.get(i) as StructRowProxy);
        // Tiles overlap in what they hold at different depths; a landmark must be listed once.
        if (seen.has(datum.row)) continue;
        seen.add(datum.row);
        found.push(datum);
      }
    }
    return found.sort((a, b) => a.row - b.row);
  }

  private landmarkColumn: string | null = null;
  private landmarkRows: PointDatum[] = [];
  /** The pending `settleLandmarks` timer, so a second switch cancels the first's (`V111`). */
  private landmarkRetry: ReturnType<typeof setTimeout> | null = null;
  private filter: { column: string; values: readonly unknown[] } | null = null;

  /**
   * The landmark overlay's container, created lazily inside the plot's own element.
   *
   * **Eight known points on a 100,000-point map is a legibility problem, not a colour problem**
   * (`V18`). The same size in a different colour disappears into the cloud at zoom 1, which is
   * precisely where a first-time reader needs them; size and a text label are what separate them.
   */
  private overlay: HTMLElement | null = null;

  onPointClick(cb: (datum: PointDatum) => void): void {
    this.pointHandlers.push(cb);
  }

  onEmptyClick(cb: (xy: [number, number]) => void): void {
    this.emptyHandlers.push(cb);
  }

  /**
   * **One listener on the container, and deepscatter's own `click_function` is deliberately not
   * used.**
   *
   * Its runtime only fires for clicks on the hover annotation `div` it creates for the highlit
   * point, and it hands the handler a `Qid` while its declaration promises a `StructRowProxy`
   * (hazard 4 above). Owning the listener gives one code path for both questions §8 keeps
   * separate — a rendered point and an empty coordinate — and makes "issues no request" (`V14`)
   * a property of code in this repository rather than of a library's event plumbing.
   *
   * `color_pick` is the GPU's own hit test: it renders one pixel to an off-screen framebuffer with
   * the point ids as colours and reads it back. It is exact to what is drawn, needs no spatial
   * index, and **touches no network** — `getQids` reads the already-loaded `record_batch`.
   */
  private attachClicks(): void {
    if (this.detachClicks) return;
    const node = document.querySelector(this.selector) as HTMLElement | null;
    if (node === null) return;

    const onClick = (event: MouseEvent) => {
      const target = event.currentTarget as HTMLElement;
      const box = target.getBoundingClientRect();
      const px = event.clientX - box.left;
      const py = event.clientY - box.top;

      const datum = this.pickAt(px, py);
      if (datum !== null) {
        for (const cb of this.pointHandlers) cb(datum);
        return;
      }
      const xy = this.toData(px, py);
      if (xy === null) return;
      for (const cb of this.emptyHandlers) cb(xy);
    };

    node.addEventListener('click', onClick);
    this.detachClicks = () => node.removeEventListener('click', onClick);
  }

  private pickAt(px: number, py: number): PointDatum | null {
    const renderer = this.plot._renderer;
    if (renderer === undefined) return null;
    let qid: [number, number] | null = null;
    try {
      qid = renderer.color_pick(px, py);
    } catch {
      return null;
    }
    if (qid === null) return null;
    try {
      const [row] = this.plot.deeptable.getQids([qid]);
      if (row === undefined) return null;
      const datum = toDatum(row);
      // **A tile drawn before its identity columns arrived is not clickable yet, and saying so is
      // the honest handling.** The alternative is a pane reading "row undefined", which is what
      // happened before `prefetch` existed. Reporting a miss sends the click down the empty-space
      // path, where inspect mode selects nothing — never a fetch, never a half-built datum.
      return this.isReady(datum as unknown as Record<string, unknown>) ? datum : null;
    } catch {
      // The tile left the flat tree between the pick and the lookup. Treat as a miss rather than
      // as an error: a click that resolves to nothing is a defined outcome here (§8).
      return null;
    }
  }

  /** Screen pixels -> data coordinates, through the zoom's own rescaled scales. */
  private toData(px: number, py: number): [number, number] | null {
    const zoom = this.plot._zoom;
    if (zoom === undefined) return null;
    try {
      const { x_, y_ } = zoom.scales();
      return [x_.invert(px), y_.invert(py)];
    } catch {
      return null;
    }
  }

  /**
   * §8's inspect-mode primitive: the nearest point **among the loaded tiles**.
   *
   * **Loaded-only is correct rather than a compromise.** The loaded tiles *are* the visible
   * region, and a nearest-over-the-whole-corpus answer at high zoom would select something off
   * screen. `null` when nothing is near, and inspect mode then selects nothing rather than
   * reaching for a distant point.
   */
  nearestRendered(xy: [number, number]): PointDatum | null {
    const renderer = this.plot._renderer;
    if (renderer === undefined) return null;
    const [tx, ty] = xy;

    let best: StructRowProxy | null = null;
    let bestDistance = Infinity;
    for (const tile of renderer.visible_tiles()) {
      // Every identity column, not just `x`: a tile with coordinates and no sequence would win the
      // distance comparison and then have nothing to show.
      if (!IDENTITY_COLUMNS.every((column) => tile.hasLoadedColumn(column))) continue;
      const batch = tile.record_batch as RecordBatch;
      const xs = batch.getChild('x');
      const ys = batch.getChild('y');
      if (xs === null || ys === null) continue;
      for (let i = 0; i < batch.numRows; i++) {
        const dx = (xs.get(i) as number) - tx;
        const dy = (ys.get(i) as number) - ty;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestDistance) {
          bestDistance = d2;
          best = batch.get(i) as StructRowProxy;
        }
      }
    }
    return best === null ? null : toDatum(best);
  }

  destroy(): void {
    if (this.detachClicks) this.detachClicks();
    this.detachClicks = null;
    this.pointHandlers = [];
    this.emptyHandlers = [];
    this.colour = null;
    this.landmarkColumn = null;
    this.landmarkRows = [];
    if (this.landmarkRetry !== null) clearTimeout(this.landmarkRetry);
    this.landmarkRetry = null;
    this.overlay?.remove();
    this.overlay = null;
    this.clearPins();
    this.pinOverlay?.remove();
    this.pinOverlay = null;
    if (this.markRetry !== null) clearTimeout(this.markRetry);
    this.markRetry = null;
    this.clearMarks();
    this.markOverlay?.remove();
    this.markOverlay = null;
    this.clearCurated();
    this.curatedOverlay?.remove();
    this.curatedOverlay = null;
    this.filter = null;
    this.deeptables.clear();
    this.plot.destroy();
    this.loaded = false;
  }
}

/**
 * A renderer bound to one pane.
 *
 * `width`/`height` in CSS pixels. Omitted, deepscatter falls back to `window.innerWidth` and
 * `window.innerHeight`, which is right for the single full-window map and wrong for either half
 * of a side-by-side comparison — see the constructor.
 */
export function createRenderer(
  selector: string,
  width?: number,
  height?: number,
): LatentRenderer {
  return new DeepscatterRenderer(selector, width, height);
}

/**
 * What a landmark is called on the map (`V106`).
 *
 * **Three sources, in falling order of how much they actually say.**
 *
 * 1. `seed_id`, when the tile carries it. This is what the overlay was designed for: on a
 *    multi-seed corpus it names *which* seed family the landmark heads, and the label differs
 *    between landmarks.
 * 2. The landmark column's own name, with a leading `is_` removed — `is_seed` → `seed`. `V84`
 *    drops a `categorical` corpus column that holds one distinct value, because offering it as a
 *    colour channel paints the map one flat colour. `ancestor_10m_08_20` has exactly one seed, so
 *    its `seed_id` is the constant `'ancestor'` and the rule drops it — which silently took away
 *    source 1 and left the sole landmark of that atlas labelled `row 39641`. A row number is an
 *    address, not a name; it answers "which point" and not "what is this".
 * 3. The row, when there is no column name to fall back on, which is only reachable if a caller
 *    draws landmarks without having set a column.
 *
 * The `is_` strip is keyed on the name, and `V18` otherwise dispatches on the declared *role*.
 * That is deliberate and narrow: this is a display string, so a name that reads badly is a
 * cosmetic fault, whereas role-keyed dispatch decides whether a column is a palette at all.
 */
export function landmarkLabel(row: PointDatum, column: string | null): string {
  const named = row['seed_id'];
  if (named !== undefined && named !== null && String(named) !== '') return String(named);
  if (column !== null) return column.replace(/^is_/, '');
  return `row ${row.row}`;
}
